import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ESTADOS_NO_ALVO, pastaCurta, type EstadoNoAlvo, type PropostaPendente, type RotinaResumo } from '@remoto/shared';
import { CLASSES_DE_EFEITO, type ClasseDeEfeito } from '@remoto/protocolo';
import { CaixaDeMarcar, CampoDeEscolha } from '../componentes/campos';
import { IconeAviso, IconeBuscar, IconeMais } from '../componentes/icones';
import { mensagemDoErro } from '../erros';
import { usarAlvos, usarPropostas, usarRotinas } from './consultas';
import { AvisoDeErro, BotaoSecundario, usarPodeComandar } from './pecas';
import { OrigemDaProposta } from './CartaoDeProposta';
import {
  atencaoDaRotina,
  FILTRO_VAZIO,
  filtrarRotinas,
  filtrosLigados,
  FORMAS,
  hashCurto,
  ordenarRotinas,
  ROTULO_DA_CLASSE,
  ROTULO_DA_FORMA,
  ROTULO_DO_ESTADO,
  type FiltroDeRotinas,
  type FormaDaRotina,
} from './regras';
import { SeloDaClasse, SeloDoEstado, SeloNeutro } from './selos';

/**
 * A aba Rotinas: as propostas esperando decisão no alto, os filtros, e a lista
 * — com o que está em risco (suspeita, quebrada) e o que espera decisão
 * destacados e no topo.
 *
 * A lista cresce com a frota: teto em `vh` e rolagem própria (`web.md`), para
 * os filtros e as propostas nunca saírem da vista.
 */

/** O "todos" de cada filtro: o seletor não tem como escolher `null`. */
const TODOS = '*';

function opcoes<T extends string>(rotuloDoTodos: string, valores: readonly T[], rotulo: (v: T) => string) {
  return [{ id: TODOS, rotulo: rotuloDoTodos }, ...valores.map((v) => ({ id: v as string, rotulo: rotulo(v) }))];
}

/** Uma proposta de rotina na fila do alto: o que é e o caminho para revisar. */
function LinhaDeProposta({ proposta }: { proposta: Exclude<PropostaPendente, { especie: 'verbete' }> }): React.JSX.Element {
  const resumo =
    proposta.especie === 'versao'
      ? `versão nova v${proposta.proposta.numero} — ${proposta.proposta.motivo}`
      : `estender ao alvo ${proposta.alvo} — ${proposta.semelhanca}`;
  const propostaPor = proposta.especie === 'versao' ? proposta.proposta.propostaPor : proposta.propostaPor;
  return (
    <li className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2" data-testid="proposta-na-fila">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-zinc-100">{proposta.rotina.titulo}</p>
        <p className="truncate text-xs text-zinc-400" title={resumo}>
          {resumo}
        </p>
        <p className="text-[11px] text-zinc-500">
          <OrigemDaProposta propostaPor={propostaPor} />
        </p>
      </div>
      <Link
        to="/toolkit/rotinas/$id"
        params={{ id: proposta.rotina.id }}
        search={{ proposta: proposta.id }}
        className="shrink-0 rounded-md border border-sky-700 px-2.5 py-1 text-xs text-sky-200 transition hover:bg-sky-900/40 pointer-coarse:min-h-11 pointer-coarse:px-3 pointer-coarse:py-2.5"
      >
        Revisar
      </Link>
    </li>
  );
}

function LinhaDaRotina({ rotina }: { rotina: RotinaResumo }): React.JSX.Element {
  const atencao = atencaoDaRotina(rotina);
  const risco = atencao.suspeitas.length > 0 || atencao.quebradas.length > 0;
  const VISIVEIS = 6;
  return (
    <li>
      <Link
        to="/toolkit/rotinas/$id"
        params={{ id: rotina.id }}
        data-testid="rotina"
        data-rotina={rotina.nome}
        className={
          'block min-w-0 rounded-lg border p-3 transition hover:bg-zinc-900/60 ' +
          (risco
            ? 'border-amber-700/70 bg-amber-950/10'
            : atencao.propostas > 0
              ? 'border-sky-800 bg-sky-950/10'
              : 'border-zinc-800')
        }
      >
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="min-w-0 truncate text-sm font-medium text-zinc-100">{rotina.titulo}</span>
          <span className="min-w-0 truncate font-mono text-xs text-zinc-500">{rotina.nome}</span>
          {rotina.arquivada ? <SeloNeutro>arquivada</SeloNeutro> : null}
        </div>
        <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-zinc-400">
          <SeloNeutro>{ROTULO_DA_FORMA[rotina.forma]}</SeloNeutro>
          {rotina.vigente ? (
            <span className="font-mono" title={rotina.vigente.sha256}>
              v{rotina.vigente.numero} · {hashCurto(rotina.vigente.sha256)}
            </span>
          ) : (
            <SeloDoEstado estado="sem_versao_aprovada" />
          )}
          {rotina.classes.map((c) => (
            <SeloDaClasse key={c} classe={c} />
          ))}
          {rotina.projeto ? (
            <span className="truncate text-zinc-500" title={rotina.projeto.cwdProjeto}>
              projeto {pastaCurta(rotina.projeto.cwdProjeto)}
            </span>
          ) : null}
        </div>
        {atencao.propostas > 0 || risco ? (
          <p className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            {atencao.propostas > 0 ? (
              <span className="text-sky-300" data-testid="selo-propostas">
                {atencao.propostas === 1 ? '1 proposta esperando decisão' : `${atencao.propostas} propostas esperando decisão`}
              </span>
            ) : null}
            {atencao.suspeitas.length > 0 ? (
              <span className="flex min-w-0 items-center gap-1 text-amber-300" data-testid="selo-suspeita">
                <IconeAviso tamanho={12} />
                <span className="truncate">suspeita em {atencao.suspeitas.join(', ')}</span>
              </span>
            ) : null}
            {atencao.quebradas.length > 0 ? (
              <span className="truncate text-red-300">quebrada em {atencao.quebradas.join(', ')}</span>
            ) : null}
          </p>
        ) : null}
        {rotina.matriz.length > 0 ? (
          <ul className="mt-2 flex min-w-0 flex-wrap gap-1.5" aria-label="Estado por alvo">
            {rotina.matriz.slice(0, VISIVEIS).map((c) => (
              <li key={c.alvo} className="min-w-0 max-w-full">
                <SeloDoEstado estado={c.estado} prefixo={c.alvo} />
              </li>
            ))}
            {rotina.matriz.length > VISIVEIS ? (
              <li className="text-[11px] text-zinc-500">+{rotina.matriz.length - VISIVEIS} alvos</li>
            ) : null}
          </ul>
        ) : (
          <p className="mt-2 text-[11px] text-zinc-500">não estendida a nenhum alvo</p>
        )}
      </Link>
    </li>
  );
}

export function AbaRotinas(): React.JSX.Element {
  const podeComandar = usarPodeComandar();
  const [arquivadas, setArquivadas] = useState(false);
  const [filtro, setFiltro] = useState<FiltroDeRotinas>(FILTRO_VAZIO);
  const rotinas = usarRotinas(arquivadas);
  const propostas = usarPropostas();
  const alvos = usarAlvos();

  const daFila = (propostas.data ?? []).filter(
    (p): p is Exclude<PropostaPendente, { especie: 'verbete' }> => p.especie !== 'verbete',
  );
  const visiveis = useMemo(() => ordenarRotinas(filtrarRotinas(rotinas.data ?? [], filtro)), [rotinas.data, filtro]);
  const alvosConhecidos = useMemo(() => {
    const chaves = new Set((alvos.data ?? []).map((a) => a.chave));
    for (const r of rotinas.data ?? []) for (const c of r.matriz) chaves.add(c.alvo);
    return [...chaves].sort();
  }, [alvos.data, rotinas.data]);
  const mudar = (parcial: Partial<FiltroDeRotinas>): void => setFiltro((f) => ({ ...f, ...parcial }));
  const deTodos = <T extends string>(v: string): T | null => (v === TODOS ? null : (v as T));

  return (
    <div className="space-y-4">
      {daFila.length > 0 ? (
        <section aria-label="Esperando decisão" className="rounded-lg border border-sky-800 bg-sky-950/20">
          <h2 className="border-b border-sky-900/60 px-3 py-2 text-sm font-medium text-sky-100">
            Esperando decisão ({daFila.length})
          </h2>
          <ul className="max-h-[30vh] divide-y divide-sky-900/40 overflow-y-auto">
            {daFila.map((p) => (
              <LinhaDeProposta key={p.id} proposta={p} />
            ))}
          </ul>
        </section>
      ) : null}

      <div className="flex flex-wrap items-end gap-2" aria-label="Filtros">
        <label className="flex min-w-0 flex-1 basis-48 items-center gap-2 rounded-md border border-zinc-700 bg-zinc-950 px-2.5 py-1.5 focus-within:border-zinc-500">
          <IconeBuscar tamanho={14} className="text-zinc-500" />
          <input
            value={filtro.texto}
            onChange={(e) => mudar({ texto: e.target.value })}
            placeholder="Buscar rotina pelo nome"
            aria-label="Buscar rotina pelo nome"
            className="min-w-0 flex-1 bg-transparent text-sm text-zinc-100 outline-none placeholder:text-zinc-600 pointer-coarse:text-base"
          />
        </label>
        <CampoDeEscolha
          rotulo="Forma"
          rotuloOculto
          compacto
          valor={filtro.forma ?? TODOS}
          opcoes={opcoes('todas as formas', FORMAS, (f) => ROTULO_DA_FORMA[f])}
          aoEscolher={(v) => mudar({ forma: deTodos<FormaDaRotina>(v) })}
        />
        <CampoDeEscolha
          rotulo="Classe"
          rotuloOculto
          compacto
          valor={filtro.classe ?? TODOS}
          opcoes={opcoes('todas as classes', CLASSES_DE_EFEITO, (c) => ROTULO_DA_CLASSE[c])}
          aoEscolher={(v) => mudar({ classe: deTodos<ClasseDeEfeito>(v) })}
        />
        <CampoDeEscolha
          rotulo="Alvo"
          rotuloOculto
          compacto
          valor={filtro.alvo ?? TODOS}
          opcoes={opcoes('todos os alvos', alvosConhecidos, (a) => a)}
          aoEscolher={(v) => mudar({ alvo: deTodos(v) })}
        />
        <CampoDeEscolha
          rotulo="Estado"
          rotuloOculto
          compacto
          valor={filtro.estado ?? TODOS}
          opcoes={opcoes('todos os estados', ESTADOS_NO_ALVO, (e) => ROTULO_DO_ESTADO[e])}
          aoEscolher={(v) => mudar({ estado: deTodos<EstadoNoAlvo>(v) })}
        />
        {filtrosLigados(filtro) > 0 ? (
          <BotaoSecundario onClick={() => setFiltro(FILTRO_VAZIO)} className="text-xs">
            Limpar filtros
          </BotaoSecundario>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <CaixaDeMarcar marcada={arquivadas} aoMarcar={setArquivadas} className="text-xs text-zinc-400">
          mostrar arquivadas
        </CaixaDeMarcar>
        {podeComandar ? (
          <Link
            to="/toolkit/rotinas/nova"
            className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-sky-500 pointer-coarse:min-h-11"
          >
            <IconeMais tamanho={14} />
            Nova rotina
          </Link>
        ) : null}
      </div>

      {rotinas.isLoading ? <p className="text-sm text-zinc-500">carregando…</p> : null}
      {rotinas.error ? <AvisoDeErro>{mensagemDoErro(rotinas.error, 'Não foi possível carregar as rotinas agora.')}</AvisoDeErro> : null}
      {rotinas.data && visiveis.length === 0 ? (
        <p className="text-sm text-zinc-500">
          {rotinas.data.length === 0 ? 'Nenhuma rotina no catálogo ainda.' : 'Nenhuma rotina com estes filtros.'}
        </p>
      ) : null}
      {visiveis.length > 0 ? (
        <ul data-testid="lista-de-rotinas" className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {visiveis.map((r) => (
            <LinhaDaRotina key={r.id} rotina={r} />
          ))}
        </ul>
      ) : null}
    </div>
  );
}
